import {
	createSlice,
	createSelector,
	createAsyncThunk,
	createEntityAdapter,
} from "@reduxjs/toolkit";
import dayjs from "dayjs";
import { newId } from "utils/common";
import { getWorkflowHistoryByFormId, postWorkflowHistory } from "api";
import { selectForm } from "./formSlice";

const workflowHistoryAdapter = createEntityAdapter();

const initialState = workflowHistoryAdapter.getInitialState({
	status: "idle",
	error: null,
});

export const postWorkflowHistoryThunk = createAsyncThunk(
	"workflowHistory/postWorkflowHistory",
	async (
		{ workflowId, action, description, category, comment, userId },
		{ rejectWithValue, getState, dispatch }
	) => {
		try {
			const state = getState();
			const responseFormId = selectForm(state)?.id;
			const workflowHistory = {
				responseFormId,
				workflowId,
				action,
				description,
				category,
				comment,
				userId,
			};
			await postWorkflowHistory(workflowHistory);
			const tempId = newId();
			dispatch(
				workflowHistoryAdded({
					id: tempId,
					createOn: dayjs().format("YYYY-MM-DDTHH:mm:ss"),
					...workflowHistory,
				})
			);
		} catch (error) {
			console.error(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

export const fetchWorkflowHistoryByFormId = createAsyncThunk(
	"workflowHistory/fetchWorkflowHistoryByFormId",
	async ({ responseFormId }, { rejectWithValue, dispatch }) => {
		try {
			dispatch(
				workflowHistoryReceived(
					await getWorkflowHistoryByFormId({
						responseFormId,
					})
				)
			);
		} catch (error) {
			console.error(error.message);
			return rejectWithValue({
				title: error.name,
				message: error.message,
				status: "",
			});
		}
	}
);

const workflowHistorySlice = createSlice({
	name: "workflowHistory",
	initialState,
	reducers: {
		workflowHistoryAdded(state, action) {
			workflowHistoryAdapter.addOne(state, action.payload);
		},
		workflowHistoryReceived(state, action) {
			workflowHistoryAdapter.setAll(state, action.payload);
		},
		workflowHistoryStatusEdited(state, action) {
			state.status = action.payload;
		},
	},
	extraReducers: {
		RESET_CME: (state, action) => {
			return initialState;
		},
		[fetchWorkflowHistoryByFormId.pending]: (state, action) => {
			state.status = "loading";
		},
		[fetchWorkflowHistoryByFormId.fulfilled]: (state, action) => {
			state.status = "succeeded";
		},
		[fetchWorkflowHistoryByFormId.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
		[postWorkflowHistoryThunk.pending]: (state, action) => {
			state.status = "loading";
		},
		[postWorkflowHistoryThunk.fulfilled]: (state, action) => {
			state.status = "succeeded";
		},
		[postWorkflowHistoryThunk.rejected]: (state, action) => {
			state.status = "failed";
			state.error = action.payload;
		},
	},
});
export const {
	workflowHistoryReceived,
	workflowHistoryStatusEdited,
	workflowHistoryAdded,
} = workflowHistorySlice.actions;

/*
SELECTORS
*/
export const {
	selectAll: selectAllWorkflowHistoryRaw,
	selectById: selectWorkflowHistoryById,
} = workflowHistoryAdapter.getSelectors((state) => state.workflowHistory);

export const selectAllWorkflowHistory = createSelector(
	[selectAllWorkflowHistoryRaw],
	(wh) => {
		return wh.sort((a, b) =>
			a.createOn < b.createOn ? 1 : a.createOn > b.createOn ? -1 : 0
		);
	}
);

export const selectWorkflowHistoryByWorkflowId = (state, props) =>
	state.workflowHistory;

export const selectWorkflowHistoryError = (state) =>
	state.workflowHistory.error;

export const selectLastSubmit = createSelector(
	[selectAllWorkflowHistory],
	(wh) => {
		//sort by latest created, because a form can be submitted multiple times
		return wh
			.filter((a) => a.action === "Submit" || a.action === "Resume")
			.sort((a, b) =>
				a.createOn < b.createOn ? 1 : a.createOn > b.createOn ? -1 : 0
			)?.[0];
	}
);

export const selectAllWorkflowHistoryIds = createSelector(
	[selectAllWorkflowHistory],
	(wh) => {
		//sort by latest created, because a form can be submitted multiple times
		return wh
			.sort((a, b) =>
				a.createOn < b.createOn ? 1 : a.createOn > b.createOn ? -1 : 0
			)
			.map((d) => d.id);
	}
);

export const selectCommentByWorkflowId = createSelector(
	[selectAllWorkflowHistory, (_, { id }) => id],
	(wh, id) => {
		return wh
			.filter(
				(a) =>
					a.action !== "Email" && //ignore email workflow history
					a.action !== "Submit" && //submitter comment shows under "Comments from Submitter", not the first approver
					a.action !== "Resume" &&
					a.workflowId === id
			)
			.sort((a, b) =>
				a.createOn < b.createOn ? 1 : a.createOn > b.createOn ? -1 : 0
			)?.[0]?.comment;
	}
);

export default workflowHistorySlice.reducer;
